import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { withApollo, graphql } from 'react-apollo';
import { connect } from 'react-redux';

import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import ErrorView from '../../../../components/global/ErrorView';
import { MeForAlbumFolderLinkRequestProcess } from '../../../../graphql/queries/album-folder';
import {
  albumFolderLinkRequestAccept as albumFolderLinkRequestAcceptMutation,
  albumFolderLinkRequestReject as albumFolderLinkRequestRejectMutation,
} from '../../../../graphql/mutations/picture-day';
import { getErrorString } from '../../../../util/normalize-errors';
import { getBasenameFromEntity } from '../../../../util/role-definitions';
import { openAlert as openAlertAction } from '../../../../actions/modal-actions';

const propTypes = {
  albumFolderLinkRequest: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolderLinkRequestAccept: PropTypes.func,
  albumFolderLinkRequestReject: PropTypes.func,
  loading: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumFolderLinkRequestId: PropTypes.string,
      status: PropTypes.oneOf(['accept', 'reject']),
    }),
  }),
  openAlert: PropTypes.func,
};

const AlbumFolderLinkRequestRedirect = ({
  albumFolderLinkRequest,
  albumFolderLinkRequestAccept,
  albumFolderLinkRequestReject,
  loading,
  history,
  match,
  openAlert,
}) => {
  const [error, setError] = useState('');
  const { status } = match.params;

  const processAlbumFolderLinkRequest = async () => {
    try {
      const isSenderSource =
        albumFolderLinkRequest.album_folder.account.id ===
        albumFolderLinkRequest.sender_account.id;

      const albumFolder = isSenderSource
        ? albumFolderLinkRequest.album_folder
        : albumFolderLinkRequest.target_album_folder;

      if (status === 'accept') {
        await albumFolderLinkRequestAccept({
          album_folder_link_request_id: albumFolderLinkRequest?.id,
        });

        openAlert({
          type: 'success',
          message: `Link request for ${albumFolder.name} has been accepted`,
        });
      } else {
        await albumFolderLinkRequestReject({
          album_folder_link_request_id: albumFolderLinkRequest?.id,
        });

        openAlert({
          type: 'success',
          message: `Link request for ${albumFolder.name} has been rejected`,
        });
      }

      history.push(
        `/app/${getBasenameFromEntity(albumFolder)}/folders/${albumFolder.id}`,
      );
    } catch (err) {
      setError(getErrorString(err));
    }
  };

  useEffect(() => {
    if (albumFolderLinkRequest?.id) {
      processAlbumFolderLinkRequest();
    }
  }, [albumFolderLinkRequest?.id]);

  useEffect(() => {
    if (!loading && !albumFolderLinkRequest) {
      setError(
        'We could not find any link request with the provided attributes.',
      );
    }
  }, [loading]);

  if (error) {
    return (
      <ErrorView
        location={{
          state: {
            error,
          },
        }}
      />
    );
  }

  return <FullScreenLoader />;
};

AlbumFolderLinkRequestRedirect.propTypes = propTypes;

export { AlbumFolderLinkRequestRedirect as AlbumFolderLinkRequestRedirectComp };

const withMeForAlbumFolderLinkRequestProcess = graphql(
  MeForAlbumFolderLinkRequestProcess,
  {
    options: ({ match }) => ({
      variables: {
        album_folder_link_request_id: match.params.albumFolderLinkRequestId,
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { me, loading } }) => ({
      loading,
      user: me,
      albumFolderLinkRequest: me?.album_folder_link_requests?.records?.[0],
    }),
  },
);

const withAlbumFolderLinkRequestAccept = graphql(
  albumFolderLinkRequestAcceptMutation,
  {
    props: ({ mutate }) => ({
      albumFolderLinkRequestAccept: ({
        album_folder_link_request_id: albumFolderLinkRequestId,
      }) =>
        mutate({
          variables: {
            input: {
              album_folder_link_request_id: albumFolderLinkRequestId,
            },
          },
        }),
    }),
  },
);

const withAlbumFolderLinkRequestReject = graphql(
  albumFolderLinkRequestRejectMutation,
  {
    props: ({ mutate }) => ({
      albumFolderLinkRequestReject: ({
        album_folder_link_request_id: albumFolderLinkRequestId,
      }) =>
        mutate({
          variables: {
            input: {
              album_folder_link_request_id: albumFolderLinkRequestId,
            },
          },
        }),
    }),
  },
);

const mapDispatchToProps = {
  openAlert: openAlertAction,
};

export default compose(
  withApollo,
  withRouter,
  connect(null, mapDispatchToProps),
  withMeForAlbumFolderLinkRequestProcess,
  withAlbumFolderLinkRequestAccept,
  withAlbumFolderLinkRequestReject,
)(AlbumFolderLinkRequestRedirect);
